import React, { useState, useEffect } from 'react'
import { ArrowLeft, Trophy, Zap, Clock, Sparkles } from 'lucide-react'

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5)
}

function makeProblem(difficulty = 1) {
  const ops = ['+', '×', '-']
  const op = ops[Math.floor(Math.random() * ops.length)]
  let a, b, answer

  if (op === '+') {
    a = Math.floor(Math.random() * (15 * difficulty)) + 5
    b = Math.floor(Math.random() * (15 * difficulty)) + 5
    answer = a + b
  } else if (op === '×') {
    a = Math.floor(Math.random() * 12) + 2
    b = Math.floor(Math.random() * 9) + 2
    answer = a * b
  } else {
    a = Math.floor(Math.random() * (20 * difficulty)) + 10
    b = Math.floor(Math.random() * a) + 2
    answer = a - b
  }

  const wrongOptions = new Set()
  while (wrongOptions.size < 3) {
    const delta = (Math.floor(Math.random() * 5) + 1) * (Math.random() > 0.5 ? 1 : -1)
    const val = answer + delta
    if (val !== answer && val >= 0) wrongOptions.add(val)
  }

  return {
    text: `${a} ${op} ${b}`,
    answer,
    options: shuffle([answer, ...Array.from(wrongOptions)]),
  }
}

export function NeuroMathGame({ onBack, onComplete }) {
  const [round, setRound] = useState(0)
  const [score, setScore] = useState(0)
  const [combo, setCombo] = useState(0)
  const [timeLeft, setTimeLeft] = useState(15)
  const [problem, setProblem] = useState(() => makeProblem(1))
  const [isFinished, setIsFinished] = useState(false)
  const [feedback, setFeedback] = useState(null)

  useEffect(() => {
    if (isFinished) return
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAnswer(null) // timeout
          return 15
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [round, isFinished])

  const handleAnswer = (val) => {
    const isCorrect = val === problem.answer
    if (isCorrect) {
      const addedScore = 50 + combo * 10
      setScore((s) => s + addedScore)
      setCombo((c) => c + 1)
      setFeedback({ correct: true, msg: `+${addedScore} BP` })
    } else {
      setCombo(0)
      setFeedback({ correct: false, msg: '¡Error!' })
    }

    setTimeout(() => {
      setFeedback(null)
      if (round + 1 >= 5) {
        setIsFinished(true)
        onComplete?.(score + (isCorrect ? 50 : 0))
      } else {
        setRound((r) => r + 1)
        setTimeLeft(15)
        setProblem(makeProblem(round >= 2 ? 2 : 1))
      }
    }, 450)
  }

  if (isFinished) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 animate-bounce">
          <Trophy size={40} className="text-white" />
        </div>
        <div>
          <span className="px-3 py-1 rounded-full bg-cyan-950 border border-cyan-500/40 text-cyan-300 text-xs font-bold uppercase tracking-wider">
            Sesión Completada
          </span>
          <h2 className="text-2xl font-black text-white mt-2">¡Neuro-Math Blitz!</h2>
          <p className="text-slate-400 text-xs mt-1">Reflejos numéricos afilados.</p>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 w-full max-w-xs space-y-2">
          <div className="flex justify-between text-xs text-slate-400">
            <span>Puntaje Total</span>
            <span className="text-cyan-400 font-bold">+{score} BP</span>
          </div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>XP Multiplicador</span>
            <span className="text-emerald-400 font-bold">x2 MODO HYPERFOCUS</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="w-full max-w-xs py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-extrabold text-sm shadow-lg shadow-cyan-500/25 active:scale-95 transition-all cursor-pointer"
        >
          Volver al Hub
        </button>
      </div>
    )
  }

  return (
    <div className="p-4 flex flex-col min-h-[80vh] max-w-md mx-auto">
      {/* Top Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-300 hover:text-white cursor-pointer active:scale-95"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-950/80 border border-cyan-500/40 text-cyan-300 text-xs font-bold">
          <Clock size={14} className="text-cyan-400 animate-spin" />
          <span>{timeLeft}s</span>
        </div>

        <div className="flex items-center gap-1 text-xs font-bold text-cyan-400">
          <Zap size={14} />
          <span>{score} BP</span>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
        <span>Pregunta {round + 1} de 5</span>
        {combo > 1 && (
          <span className="text-amber-400 font-bold flex items-center gap-1">
            <Sparkles size={12} /> {combo}x COMBO
          </span>
        )}
      </div>
      <div className="w-full h-1.5 rounded-full bg-slate-800 mb-8 overflow-hidden">
        <div
          className="h-full bg-cyan-400 rounded-full transition-all duration-300"
          style={{ width: `${((round + 1) / 5) * 100}%` }}
        />
      </div>

      {/* Problem Display */}
      <div className="flex-1 flex flex-col items-center justify-center text-center my-6 relative">
        {feedback && (
          <div
            className={`absolute top-0 px-4 py-1.5 rounded-full text-xs font-extrabold animate-bounce ${
              feedback.correct
                ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400'
                : 'bg-rose-500/20 border border-rose-500/40 text-rose-400'
            }`}
          >
            {feedback.msg}
          </div>
        )}

        <div className="text-5xl font-black text-white tracking-widest my-4">
          {problem.text} = ?
        </div>
        <p className="text-xs text-slate-400">Calcula rápido antes de que se agote el tiempo</p>
      </div>

      {/* Options Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {problem.options.map((opt, i) => (
          <button
            key={i}
            type="button"
            onClick={() => handleAnswer(opt)}
            className="py-4 rounded-2xl bg-[#131a29] border border-slate-700 hover:border-cyan-500 hover:bg-[#182338] text-2xl font-black text-white shadow-lg active:scale-95 transition-all cursor-pointer"
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}
